import React, { useContext, useState, useEffect } from 'react';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { AppContext } from '@edx/frontend-platform/react';
import { DIRECT_PLUGIN, PLUGIN_OPERATIONS } from '@openedx/frontend-plugin-framework';

import { // Components
  Header,
  LearningHeader,
  StudioHeader,
  CommonFooter,
  BrowserMetaData,
  getUserGroupCacheData,
  getCourseDetailCacheData,
  Login
} from '@crystaldelta/xen-paragon';

const AuthWrap = ({ Component, needUserRole, ...props }) => {
  // Used to send Authenticaed User details to Xen-Paragon Components
  const { authenticatedUser, config } = useContext(AppContext);
  const [userDetails, setUserDetails] = useState({});
  const [courseDetails, setCourseDetails] = useState({});
  let courseId = props.courseId ? props.courseId : window.location.pathname.split('/').find(part => part.includes('course-v1:')) || null;

  if(window.location.pathname.includes('/ora-grading/')) {
    const [, blockId] = window.location.pathname.match(/ora-grading\/(block-v1:[^+]+(?:\+[^+]+)*?)\+type/);
    if (blockId) {
      courseId = blockId.replace(/^block-/, "course-");
    }
  }

  useEffect(() => {
    if (!needUserRole || !authenticatedUser) return;
    const fetchUserRoles = async () => {
      try {
        const data = await getUserGroupCacheData(getAuthenticatedHttpClient(), config, courseId);
        const { groups, ROLES, course_staff_access, course_inst_access } = data;
        const userGroups = groups.map(x => x.name);
        setUserDetails({
          ...authenticatedUser,
          groups: userGroups,
          ROLES: ROLES,

          // Add Common User Roles here
          is_staff: authenticatedUser.administrator,
          isAdmin: userGroups.includes(ROLES.ORG_ADMIN),
          isLearner: userGroups.includes(ROLES.LEARNER),
          isParent: userGroups.includes(ROLES.PARENT),
          isOrgStaff: userGroups.includes(ROLES.ORG_STAFF),
          isProgramStaff: userGroups.includes(ROLES.PROG_STAFF),
          isProgramAdmin: userGroups.includes(ROLES.PROG_ADMIN),
          isChild: userGroups.includes(ROLES.CHILD),
          isMinor: userGroups.includes(ROLES.MINOR),
          isMajor: userGroups.includes(ROLES.MAJOR),
          isTeacher: userGroups.includes(ROLES.INSTRUCTOR),
          isManager: userGroups.includes('Manager'),
          isCourseStaffAccess: course_staff_access,
          isCourseInstructorAccess: course_inst_access,
          isInAllAdminRole: userGroups.some((role) => ROLES.ALL_ADMIN_ROLE.includes(role)),
          isInAllLearnerRole: userGroups.some((role) => ROLES.ALL_LEARNER_ROLE.includes(role)),
          isInAllOrgAdminStaffRole: userGroups.some((role) => ROLES.ALL_ORG_ADMIN_STAFF.includes(role)),
          isInAllAdminStaffRole: userGroups.some((role) => ROLES.ALL_ADMIN_STAFF.includes(role)),
          isInAllProgAdminStaffRole: userGroups.some((role) => ROLES.ALL_PROG_ADMIN_STAFF.includes(role)),
          isTrainer: userGroups.includes(ROLES.TRAINER),
        });
        const courseData = await getCourseDetailCacheData(getAuthenticatedHttpClient(), config, courseId);
        setCourseDetails({
          enable_learner_progress: courseData?.enable_learner_progress || false,
          curriculum_type: courseData?.curriculum_type || null,
          course_banner_img: courseData?.course_banner_img || null,
          courseTitle: courseData?.name || null
        });
      } catch (err) {
        console.log("There was an error while cache group and course", err)
      }
    }
    fetchUserRoles();
  }, [needUserRole]);

  return <Component
    siteConfig={config}
    authUser={userDetails}
    courseDetails={courseDetails}
    getAuthenticatedHttpClient={getAuthenticatedHttpClient}

    {...props}
  />;
};

const MetaDataWrap = ({ Component, ...props }) => {
  const { config } = useContext(AppContext);
  const loginSession = new URL(`${config.LMS_BASE_URL}/xened/api/session/status/`);
  const customStylesUrl = new URL(`${config.LMS_BASE_URL}/xened/saas/api/v1/custom_code`);

  const [customStyles, setCustomStyles] = useState({ header_code: null, footer_code: null, h5p_code: null });

  useEffect(() => {
    const fetchCustomStyles = async () => {
      const currentPath = window.location.pathname;
      const enableSingleSession = config.ENABLE_SINGLE_SESSION || false;

      const courseIdMatch = window.location.pathname.match(/course-v1:[^/]+/);
      const courseId = courseIdMatch ? courseIdMatch[0] : null;

      if (!currentPath.startsWith("/authn/") && enableSingleSession) {
        try {
          if (courseId) {
            loginSession.searchParams.set('course_id', courseId);
          }
          const data = await getAuthenticatedHttpClient().get(loginSession.href);
        } catch (err) {
          const isVeritas = config.course_org_filter == "VERITAS";
          if (isVeritas) {
            try {
              const customVeritasLogout = `${config.LMS_BASE_URL}/xened/dashboard/logout_veritas_user`;
              await getAuthenticatedHttpClient().get(customVeritasLogout);
              window.location.href = `${config.VERITAS_LOGOUT_URL}`;
            } catch (err) {
              console.error('Logout failed:', err);
            }
          } else {
            window.location.href = `${config.LMS_BASE_URL}/logout`
          }
        }
      }

      try {
        const { data: { custom_code } } = await getAuthenticatedHttpClient().get(customStylesUrl.href);
        setCustomStyles(custom_code);
      } catch (err) {}
    }
    fetchCustomStyles();
  }, [])

  return <Component
    siteConfig={config}
    header_code={customStyles.header_code}
    footer_code={customStyles.footer_code}
    h5p_code = {customStyles.h5p_code}

    {...props}
  />
};

const hideAndInsertPlugin = (config = {}) => ({
  keepDefault: true,
  plugins: [
    { op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' },
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'custom-component',
        type: DIRECT_PLUGIN,
        priority: 1,
        RenderWidget: (pluginProps) => <AuthWrap Component={null} {...pluginProps} />,
        ...config
      },
    },
  ]
});

const insertPlugin = (config = {}) => ({
  keepDefault: true,
  plugins: [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'custom-component',
        type: DIRECT_PLUGIN,
        priority: 1,
        RenderWidget: (pluginProps) => <AuthWrap Component={null} {...pluginProps} />,
        ...config
      },
    },
  ]
});

const config = {
  pluginSlots: {
    metadata_slot: insertPlugin({
      id: 'browser-metadata',
      RenderWidget: (pluginProps) => <MetaDataWrap Component={BrowserMetaData} {...pluginProps} />
    }),
    header_slot: hideAndInsertPlugin({
      id: 'custom-header',
      RenderWidget: (pluginProps) => <AuthWrap Component={Header} needUserRole {...pluginProps} /> 
    }),
    learning_header_slot: hideAndInsertPlugin({
      id: 'custom-learning-header',
      RenderWidget: (pluginProps) => <AuthWrap Component={LearningHeader} needUserRole {...pluginProps} />
    }),
    studio_header_slot: hideAndInsertPlugin({
      id: 'custom-studio-header',
      RenderWidget: (pluginProps) => <AuthWrap Component={StudioHeader} needUserRole {...pluginProps} />
    }),
    footer_slot: hideAndInsertPlugin({
      id: 'custom-footer',
      RenderWidget: (pluginProps) => <AuthWrap Component={CommonFooter} needUserRole {...pluginProps} />
    }),
    login_slot: hideAndInsertPlugin({
      id: 'custom-login',
      RenderWidget: (pluginProps) => <AuthWrap Component={Login} needUserRole {...pluginProps} />
    })    
  }
};

export default config;
